"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Building2, Sparkles, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const seasonalityCycles = [{
  id: 1,
  period: "Corporate Peak",
  timing: "Monday – Thursday",
  focus: "High ADR & Corporate Yield",
  detail: "Intense business transient and MNC travel across Cyber City, Udyog Vihar, and Golf Course Road. Demand compression allows aggressive yield thresholds and premium room category monetization.",
  badge: "Peak Yield"
}, {
  id: 2,
  period: "Weekend Repositioning",
  timing: "Friday – Sunday",
  focus: "Staycations & Social Bookings",
  detail: "Corporate check-outs drop occupancy. Dynamic packaging, targeted D2C staycation offers, and flexible dynamic rate tiers capture Delhi NCR drive-in leisure guests and family weekenders.",
  badge: "Volume Capture"
}, {
  id: 3,
  period: "Winter MICE & Social",
  timing: "October – March",
  focus: "Banquet & High-Value Group Stays",
  detail: "Peak banquet, summit, and luxury wedding demand. Minimum-length-of-stay (MLOS) rules and non-refundable group allocations protect high-demand dates against single-night displacement.",
  badge: "Compression Driver"
}];
const challenges = [{
  id: 1,
  title: "Midweek Corporate Compression vs Weekend Drop",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Capitalize on high corporate demand between Monday and Thursday while deploying targeted weekend yield strategies to eliminate Sunday vacancy dips across NCR staycationers.",
  metricLabel: "Strategy focus",
  metricValue: "Day-of-week rate fencing & leisure package bundling"
}, {
  id: 2,
  title: "Aggressive Tech-Park Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Monitor intraday rate shifts across Cyber Hub, Golf Course Extension, and MG Road. Automated competitor tracking and dynamic yield floors protect RevPAR before competitor rate dumping occurs.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA rate intelligence & automated fences"
}, {
  id: 3,
  title: "Corporate RFP Leakage & OTA Parity Gaps",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Prevent negotiated corporate rates from leaking into unauthorized B2B OTA channels. Maintain ironclad rate parity across Google Hotels and OTAs while incentivizing direct company booking portals.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate containment & direct corporate booking"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapegurgaon" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Gurgaon
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Gurgaon is North India’s preeminent corporate and financial power corridor, defined by high-volume corporate weekday demand, rapid lead times, and weekend rate compression across Cyber City, Golf Course Road, and Sohna Road. Sunil Singh crafts precise yield algorithms and corporate channel strategies designed for premium business hotels and boutique stays.
          </p>
        </div>

        {/* Seasonality & Demand Dynamics Block */}
        <div className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 pb-4 border-b border-border">
            <div>
              <span className="text-xs font-mono font-semibold text-secondary uppercase tracking-widest">
                Gurgaon Demand Cycles
              </span>
              <h3 className="font-serif text-2xl font-bold text-foreground mt-1">
                Seasonality & Yield Behavior
              </h3>
            </div>
            <p className="text-sm text-muted-foreground mt-2 sm:mt-0 max-w-md">
              Balancing midweek MNC corporate compression with weekend leisure and wedding group capture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityCycles.map((season, index) => <div key={season.id} data-index={index} className="relative flex flex-col justify-between p-6 rounded-xl border border-border bg-card/70 hover:border-secondary/50 transition-[border-color,box-shadow] duration-300">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-secondary/15 text-secondary-foreground border border-secondary/20">
                      {season.badge}
                    </span>
                    <span className="text-xs font-mono text-muted-foreground">
                      {season.timing}
                    </span>
                  </div>
                  <h4 className="font-serif text-lg font-bold text-foreground mb-1">
                    {season.period}
                  </h4>
                  <p className="text-xs font-semibold text-secondary mb-3">
                    {season.focus}
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {season.detail}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* 3-Column Card Grid - Strategic Challenges */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Gurgaon hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}